import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { App, Badge, Button, Skeleton, Tabs, Typography } from 'antd';
import { ReactComponent as NotifBellIcon } from 'assets/icons/NotifBell.svg';
import classNames from 'clsx';
import SidePanel from 'components/SidePanel';
import NotificationListItem from 'components/notifications/NotificationListItem';
import Colors from 'const/colors';
import { useNotificationsPanel } from 'contexts/NotificationsPanelContext';
import { useNotifications } from 'hooks/useNotifications';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Notification, NotificationStatus } from 'types/Notification';

const DRAWER_WIDTH = 426;
const PAGE_SIZE = 10;

export const NotificationPopover = () => {
  const { t } = useTranslation();
  const { isOpen, openPanel, closePanel } = useNotificationsPanel();
  const [activeTab, setActiveTab] = useState<NotificationStatus>('unread');
  const { notification } = App.useApp();

  const {
    notifications,
    counts,
    hasMore,
    isLoading,
    isSuccess,
    isFetchingNextPage,
    fetchNextPage,
    markAllAsRead,
    markNotificationAsRead,
  } = useNotifications({
    status: activeTab,
    limit: PAGE_SIZE,
  });

  const markAllNotificationsAsRead = () => {
    Promise.resolve(markAllAsRead()).catch(() => undefined);
  };

  const markNotificationAsReadById = (notificationId: string) => {
    markNotificationAsRead(notificationId).catch(() => {
      notification.error({
        message: t('global:internet_connexion_error'),
        placement: 'bottomRight',
      });
    });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readDate) {
      markNotificationAsReadById(notification.id);
    }

    closePanel();
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isFetchingNextPage) {
      return;
    }

    const target = event.currentTarget;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 40;

    if (!isNearBottom) {
      return;
    }

    Promise.resolve(fetchNextPage()).catch(() => undefined);
  };

  const tabs = [
    {
      key: 'unread',
      label: t('notification:tabs.unread', {
        count: counts.unread,
      }),
    },
    {
      key: 'read',
      label: t('notification:tabs.read', { count: counts.read }),
    },
  ];

  return (
    <>
      <Badge
        count={counts.unread || ''}
        size="small"
        color={Colors.BleuHellio}
        className="[&_.ant-badge-count]:top-2 [&_.ant-badge-count]:right-2"
      >
        <Button
          type="text"
          icon={<BellOutlined className="text-black !text-xl" />}
          className="text-black [&_.ant-btn-icon]:!h-8 [&_.ant-btn-icon]:flex [&_.ant-btn-icon]:items-center [&_.ant-btn-icon]:justify-center"
          size="large"
          onClick={openPanel}
          aria-label={t('notification:title')}
        />
      </Badge>

      <SidePanel
        open={isOpen}
        onClose={closePanel}
        width={DRAWER_WIDTH}
        title={t('notification:title')}
        className="[&_.ant-drawer-body]:!p-0 [&_.ant-drawer-header]:!rounded-none [&_.ant-drawer-header]:!border-0 [&_.ant-drawer-header]:!bg-bleu-hellio-6 [&_.ant-drawer-header]:!px-4 [&_.ant-drawer-header]:!py-4 [&_.ant-drawer-header-title]:!text-white [&_.ant-drawer-title]:font-fieldwork [&_.ant-drawer-title]:text-base [&_.ant-drawer-title]:leading-6 [&_.ant-drawer-title]:font-semibold [&_.ant-drawer-close]:!text-white [&_.ant-drawer-body]:overflow-y-hidden"
      >
        <Tabs
          activeKey={activeTab}
          onChange={(tab) => setActiveTab(tab as NotificationStatus)}
          items={tabs}
          className="[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-nav]:!bg-white [&_.ant-tabs-nav]:!px-4 [&_.ant-tabs-tab]:!py-3 [&_.ant-tabs-tab-btn]:font-fieldwork"
          tabBarExtraContent={
            activeTab === 'unread' && counts.unread > 0 ? (
              <Button
                type="text"
                className="!text-bleu-hellio-6"
                icon={<CheckOutlined />}
                onClick={markAllNotificationsAsRead}
                aria-label={t('notification:mark_as_read')}
                title={t('notification:mark_as_read')}
              />
            ) : null
          }
        />

        {isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          </div>
        )}

        {isSuccess && notifications.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center -mt-24">
            <NotifBellIcon />
            <Typography.Text className="text-gris-clair">
              {t('notification:no_new_notification')}
            </Typography.Text>
          </div>
        )}

        <div className="max-h-full overflow-y-auto" onScroll={handleScroll}>
          {isSuccess &&
            notifications.map((notification) => {
              return (
                <NotificationListItem
                  key={notification.id}
                  notification={notification}
                  className={classNames(
                    'w-full cursor-pointer border-b border-color-border bg-white px-4 py-3 text-left',
                    'hover:bg-[#fafafa]',
                  )}
                  onClick={handleNotificationClick}
                  showDescription
                  onMarkAsRead={markNotificationAsReadById}
                />
              );
            })}

          {isSuccess && isFetchingNextPage && (
            <div className="space-y-3 p-4">
              <Skeleton active paragraph={{ rows: 2 }} title={false} />
            </div>
          )}
        </div>
      </SidePanel>
    </>
  );
};
